/**
 * Copyright (C) Firstlight Media. All rights reserved.
 * All information contained herein is and remains the property of Firstlight Media.
 * The intellectual and technical concepts contained herein are proprietary
 * to Firstlight Media. Dissemination of this information or reproduction
 * of this material is strictly forbidden unless prior written permission
 * is obtained from Firstlight Media.
 */

import { ApplicationSession } from '../../session/ApplicationSession';
import { ApplicationMetrics } from '../../model/ApplicationMetrics';
import { createError, createLogger, Error, ErrorCodes, Logger, LoggerLevel } from '@quickplay/foundation';
import { ApplicationError } from '../../model/ApplicationError';
import { User } from '../../model/User';
import { UserSession } from '../../session/UserSession';
import { UserSessionImpl } from './UserSessionImpl';
import { Content } from '../../model/Content';
import { ApplicationContainer } from '../../model/ApplicationContainer';
import { PlaybackSession } from '../../session/PlaybackSession';
import { PlaybackSessionImpl } from './PlaybackSessionImpl';
import { CustomEvent } from '../../model/CustomEvent';
import { AnalyticsReporterConfiguration } from '../configuration/AnalyticsReporterConfiguration';
import { AnalyticsReporter } from '../reporter/model/AnalyticsReporter';
import { ApplicationEvent } from '../../event/ApplicationEvent';
import { toApplicationErrorReportingData, toApplicationMetricsReportingData } from '../util/DataUtil';
import { QP_ANALYTICS_EVENTS } from '../util/QpAnalyticsEvents';
import { Provider } from '../Provider';
import { getProvider } from '../util/AnalyticsReporterUtil';
import { DatazoomAnalyticsReporterImpl } from '../reporter/DatazoomAnalyticsReporterImpl';
import { ConvivaAnalyticsReporterImpl } from '../reporter/ConvivaAnalyticsReporterImpl';
import { InitializationResponse } from '../../InitializationResponse';
import { AnalyticsErrorCodes } from '../AnalyticsErrorCodes';
import { CostOptimizationHandler } from '../costOptimization/CostOptimizationHandler';
import { ConvivaAnalyticsReporterConfiguration } from '../configuration/ConvivaAnalyticsReporterConfiguration';
import { CostOptimizationHandlerImpl } from '../costOptimization/CostOptimizationHandlerImpl';
import { PlatformAuthorizer } from '@quickplay/platform-authorizer';
import { NPAWAnalyticsReporterImpl } from '../reporter/NPAWAnalyticsReporterImpl';
import { NewRelicAnalyticsReporterImpl } from '../reporter/NewRelicAnalyticsReporterImpl';
import { NPAWAnalyticsReporter } from '../reporter/model/NPAWAnalyticsReporter';
import { PlaybackSourceType } from '../../type/PlaybackSourceType';
import { GodavariAnalyticsReporterImpl } from '../reporter/GodavariAnalyticsReporterImpl';

const TAG_NAME = 'FL_ANALYTICS';

export class ApplicationSessionImpl implements ApplicationSession {
  private static instance: ApplicationSession;
  private reporters?: { [key: string]: AnalyticsReporter };
  private loggers?: { [key: string]: Logger };
  private configurations?: { [key: string]: AnalyticsReporterConfiguration };
  private costOptimizationHandler?: CostOptimizationHandler;
  private progressUpdateIntervalsInMs?: { [key: string]: number | undefined };

  private constructor() {
    // no-op
  }

  static getInstance(): ApplicationSession {
    if (!ApplicationSessionImpl.instance) {
      ApplicationSessionImpl.instance = new ApplicationSessionImpl();
    }

    return ApplicationSessionImpl.instance;
  }

  async initialize(configuration: AnalyticsReporterConfiguration): Promise<InitializationResponse[]>;
  async initialize(configuration: AnalyticsReporterConfiguration[]): Promise<InitializationResponse[]>;
  async initialize(
    configuration: AnalyticsReporterConfiguration | AnalyticsReporterConfiguration[],
  ): Promise<InitializationResponse[]> {
    this.configurations = this.getConfigurations(Array.isArray(configuration) ? configuration : [configuration]);
    this.loggers = this.getLoggers(Array.isArray(configuration) ? configuration : [configuration]);
    this.reporters = this.getReporters(Array.isArray(configuration) ? configuration : [configuration]);
    this.progressUpdateIntervalsInMs = this.getProgressUpdateInterval(
      Array.isArray(configuration) ? configuration : [configuration],
    );

    this.costOptimizationHandler = new CostOptimizationHandlerImpl();

    if (!Object.keys(this.reporters).length) {
      return Promise.reject('Could not create any valid analytics reporter.');
    }

    const results = await Promise.allSettled(
      Object.keys(this.reporters).map(async (key: string) => {
        const reporter = this.reporters?.[key];
        const config = this.configurations?.[key];

        if (!reporter) {
          return Promise.resolve({
            analyticProvider: key as Provider,
            result: this.createAnalyticsInitializationError(key as Provider),
          });
        }

        if (!config) {
          return {
            analyticProvider: key as Provider,
            result: this.createAnalyticsInitializationError(key as Provider),
          };
        }

        try {
          await reporter.initialize(config);

          return Promise.resolve({
            analyticProvider: key as Provider,
            result: true,
          });
        } catch {
          return Promise.reject({
            analyticProvider: key as Provider,
            result: this.createAnalyticsInitializationError(key as Provider),
          });
        }
      }),
    );

    return this.getInitializationStatuses(results);
  }

  private getInitializationStatuses(results: any): InitializationResponse[] {
    return results.map((result: any) => {
      if (result.status === 'rejected') {
        if (this.loggers) {
          Object.values(this.loggers).forEach(logger => {
            logger?.log(`[ANALYTICS] Skipping ${result.reason.analyticProvider} analytics.`);
          });
        }

        delete this.reporters?.[result.reason.analyticProvider];
        delete this.loggers?.[result.reason.analyticProvider];
        delete this.configurations?.[result.reason.analyticProvider];

        return {
          analyticProvider: result.reason.analyticProvider,
          result: result.reason.result,
        };
      } else {
        return result.value;
      }
    });
  }

  start(metrics?: ApplicationMetrics): void {
    if (!this.reporters) {
      return;
    }
    Object.values(this.reporters).forEach(reporter => {
      reporter.reportEvent(
        ApplicationEvent.APP_START,
        metrics ? toApplicationMetricsReportingData(metrics, reporter.provider) : undefined,
      );
    });
  }

  getApplicationSessionId(): string | undefined {
    for (const reporter of Object.values(this.reporters ?? {})) {
      const id = reporter.getApplicationSessionId?.();
      if (id) return id;
    }
    return undefined;
  }

  interruptStart(customEvent?: CustomEvent): void {
    this.reportEvent(customEvent ? customEvent.event : ApplicationEvent.APP_BACKGROUND, customEvent?.data);
  }

  interruptEnd(customEvent?: CustomEvent): void {
    this.reportEvent(customEvent ? customEvent.event : ApplicationEvent.APP_FOREGROUND, customEvent?.data);
  }

  stop(error?: ApplicationError): void {
    if (!this.reporters) {
      return;
    }

    Object.values(this.reporters).forEach(reporter => {
      if (error) {
        const errorData = toApplicationErrorReportingData(
          error,
          reporter.provider === Provider.NEWRELIC ? Provider.NEWRELIC : undefined,
        );

        const eventType = reporter.provider === Provider.NEWRELIC ? ApplicationEvent.ERROR : ApplicationEvent.APP_ERROR;

        reporter.reportEvent(eventType, errorData);
      }

      if (reporter.provider !== Provider.NEWRELIC) {
        reporter.reportEvent(ApplicationEvent.APP_END);
      }
      reporter.reset();
    });

    this.costOptimizationHandler?.release();
  }

  addEvent(customEvent: CustomEvent): void {
    if (QP_ANALYTICS_EVENTS.includes(customEvent.event)) {
      if (!this.loggers) {
        return;
      }

      Object.values(this.loggers).forEach(logger => {
        logger?.log(
          `[ANALYTICS] The event ${customEvent.event} event is already reserved by the Quickplay Analytics Library.`,
        );
      });

      return;
    }

    this.reportEvent(customEvent.event, customEvent.data);
  }

  addErrorEvent(error: ApplicationError): void {
    if (!this.reporters) {
      return;
    }

    Object.values(this.reporters).forEach(reporter => {
      if (error) {
        const errorData = toApplicationErrorReportingData(
          error,
          reporter.provider === Provider.NEWRELIC ? Provider.NEWRELIC : undefined,
        );

        const eventType = reporter.provider === Provider.NEWRELIC ? ApplicationEvent.ERROR : ApplicationEvent.APP_ERROR;

        reporter.reportEvent(eventType, errorData);
      }
    });
  }

  createUserSession(user: User): UserSession | null {
    if (!this.reporters) {
      return null;
    }

    return new UserSessionImpl(this.reporters, user, this.loggers);
  }

  async createPlaybackSession(
    content: Content,
    playbackSource?: PlaybackSourceType,
    applicationContainer?: ApplicationContainer,
    platform?: PlatformAuthorizer,
  ): Promise<PlaybackSession> {
    if (!this.configurations || !this.reporters) {
      return Promise.reject();
    }

    const convivaConfig = this.configurations[Provider.CONVIVA];
    if (convivaConfig) {
      const convivaReporter = this.reporters[Provider.CONVIVA];
      await this.initializeCostOptimization(convivaReporter, convivaConfig, platform);
    }
    const { liveThresholdMs = 0, vodThresholdMs = 0 } = this.costOptimizationHandler ?? {};

    return new PlaybackSessionImpl(
      this.reporters,
      content,
      liveThresholdMs,
      vodThresholdMs,
      playbackSource,
      applicationContainer,
      this.loggers,
      this.progressUpdateIntervalsInMs,
    );
  }

  private async initializeCostOptimization(
    reporter: AnalyticsReporter,
    config: AnalyticsReporterConfiguration,
    platform?: PlatformAuthorizer,
  ): Promise<void> {
    if (!platform || !this.costOptimizationHandler || !reporter) {
      return Promise.resolve();
    }

    if (reporter.provider === Provider.CONVIVA) {
      const convivaConfig: ConvivaAnalyticsReporterConfiguration = config as ConvivaAnalyticsReporterConfiguration;

      if (convivaConfig.costOptimizationUrl && convivaConfig.costOptimizationParams) {
        return this.costOptimizationHandler?.initialize(
          convivaConfig.costOptimizationUrl,
          convivaConfig.costOptimizationParams,
          platform,
          convivaConfig.costOptimizationRefreshInterval,
          this.loggers?.[Provider.CONVIVA],
        );
      }
    }

    return Promise.resolve();
  }

  setMetadata(metadata: { [p: string]: any }): void {
    if (!this.reporters) {
      return;
    }

    Object.values(this.reporters).forEach(reporter => {
      reporter.reportMetadata(metadata);
    });
  }

  setCustomDimensions(
    content: { [key: number]: string | undefined },
    ad?: { [key: number]: string | undefined },
    replicateContentToAd?: boolean,
  ): void {
    if (!this.reporters) {
      return;
    }

    Object.values(this.reporters).forEach((reporter: AnalyticsReporter) => {
      // This api only applies to NPAW reporter to update custom dimensions.
      if (reporter.provider === Provider.NPAW) {
        const npawReporter = reporter as NPAWAnalyticsReporter;
        npawReporter.setCustomDimensions(content, ad, replicateContentToAd);
      }
    });
  }

  private getConfigurations(configurations: AnalyticsReporterConfiguration[]): {
    [key: string]: AnalyticsReporterConfiguration;
  } {
    let configs: { [key: string]: AnalyticsReporterConfiguration } = {};

    configurations.forEach((configuration: AnalyticsReporterConfiguration) => {
      configs = {
        ...configs,
        [getProvider(configuration)]: configuration,
      };
    });

    return configs;
  }

  private getLoggers(configurations: AnalyticsReporterConfiguration[]): { [key: string]: Logger } {
    let logs: { [key: string]: Logger } = {};

    configurations.forEach((configuration: AnalyticsReporterConfiguration) => {
      if (!configuration.logger) {
        return;
      }

      const provider = getProvider(configuration);

      logs = {
        ...logs,
        [provider]: configuration.logger || createLogger(LoggerLevel.OFF, `${TAG_NAME}_${provider.toUpperCase()}`),
      };
    });

    return logs;
  }

  private getProgressUpdateInterval(configurations: AnalyticsReporterConfiguration[]): {
    [key: string]: number | undefined;
  } {
    let progressUpdateInterval: { [key: string]: number | undefined } = {};
    configurations.forEach((configuration: AnalyticsReporterConfiguration) => {
      if (!configuration.progressUpdateIntervalInMs) {
        return;
      }
      const provider = getProvider(configuration);
      progressUpdateInterval = {
        ...progressUpdateInterval,
        [provider]: configuration.progressUpdateIntervalInMs,
      };
    });

    return progressUpdateInterval;
  }

  private getReporters(configurations: AnalyticsReporterConfiguration[]): { [key: string]: AnalyticsReporter } {
    let reporters: { [key: string]: AnalyticsReporter } = {};

    configurations.forEach((configuration: AnalyticsReporterConfiguration) => {
      const provider = getProvider(configuration);
      const analyticsReporter = this.getAnalyticsReporter(provider);

      if (!analyticsReporter) {
        return;
      }

      reporters = {
        ...reporters,
        [provider]: analyticsReporter,
      };
    });

    return reporters;
  }

  private getAnalyticsReporter(provider: Provider): AnalyticsReporter | undefined {
    if (provider === Provider.DATAZOOM) {
      return DatazoomAnalyticsReporterImpl.getInstance();
    }

    if (provider === Provider.CONVIVA) {
      return ConvivaAnalyticsReporterImpl.getInstance();
    }

    if (provider === Provider.NPAW) {
      return NPAWAnalyticsReporterImpl.getInstance();
    }

    if (provider === Provider.NEWRELIC) {
      return NewRelicAnalyticsReporterImpl.getInstance();
    }

    if (provider === Provider.GODAVARI) {
      return GodavariAnalyticsReporterImpl.getInstance();
    }

    return undefined;
  }

  private reportEvent(event: string, data?: { [p: string]: any }): void {
    if (!this.reporters) {
      return;
    }

    Object.values(this.reporters).forEach(reporter => {
      reporter.reportEvent(event, data);
    });
  }
  private createAnalyticsInitializationError = (provider: Provider): Error => {
    switch (provider) {
      case Provider.DATAZOOM:
        return createError(
          AnalyticsErrorCodes.DATAZOOM_ANALYTICS_INITIALIZATION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_ANALYTICS,
          'Initializing Datazoom Analytics has failed!',
        );

      case Provider.NEWRELIC:
        return createError(
          AnalyticsErrorCodes.NEW_RELIC_ANALYTICS_INITIALIZATION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_ANALYTICS,
          'Initializing New Relic Analytics has failed!',
        );

      case Provider.CONVIVA:
        return createError(
          AnalyticsErrorCodes.CONVIVA_ANALYTICS_INITIALIZATION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_ANALYTICS,
          'Initializing Conviva Analytics has failed!',
        );

      case Provider.NPAW:
        return createError(
          AnalyticsErrorCodes.NPAW_ANALYTICS_INITIALIZATION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_ANALYTICS,
          'Initializing NPAW Analytics has failed!',
        );

      case Provider.GODAVARI:
        return createError(
          AnalyticsErrorCodes.GODAVARI_ANALYTICS_INITIALIZATION_FAILURE | ErrorCodes.ERROR_CATEGORY_MASK_ANALYTICS,
          'Initializing Godavari Analytics has failed!',
        );

      case Provider.UNKNOWN:
      default: {
        throw new Error(`Unsupported provider: ${provider}`);
      }
    }
  };
}
